<template>
  <div class="w-[378px] h-[75vh] max-h-[640px]">
    <div class="flex flex-col gap-2 h-full">
      <div class="flex items-center">
        <NBox
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'SLTPForm',
            component: 'SLTPTutorialButton',
          }"
          class="flex items-center flex-1 px-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer"
          @click="openTutorialDialog()"
        >
          <img
            src="@/assets/images/sltp_guide.svg"
            alt="sltp"
            width="24"
            height="24"
          />
          <NText
            variant="caption1"
            weight="bold"
            class="mr-2 text-neutral-secondary"
          >
            {{ t('modules.order.SLTPGuide') }}
          </NText>
        </NBox>

        <NBox
          class="flex items-center p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer mr-2 flex-1"
          @click="openAlgorithmOrderRiskDescriptionDialog('sltp')"
        >
          <div
            class="w-6 h-6 flex items-center justify-center border border-neutral-tertiary ml-1 rounded-lg"
          >
            <NegarErrorBoldIcon size="16" class="text-warning-secondary" />
          </div>

          <NText
            variant="caption1"
            weight="bold"
            class="mr-2 text-neutral-secondary"
          >
            {{ t('orders.riskAnnouncement.title') }}
          </NText>
        </NBox>
      </div>

      <div
        class="border border-neutral-secondary rounded-md flex justify-between px-3 pt-3 pb-2"
      >
        <div class="flex flex-col justify-start items-start">
          <NText
            variant="overline"
            weight="medium"
            class="block text-neutral-secondary"
          >
            {{ t('modules.portfolio.current_price') }}
          </NText>

          <div class="flex">
            <NumberFormatter
              #="{ number }"
              :value="symbol.referencePriceVariationPercentage / 100"
              maximum-fraction-digits="2"
              sign-display="exceptZero"
              number-style="percent"
            >
              <NText
                :key="symbol.referencePriceVariationPercentage"
                dir="ltr"
                variant="body"
                weight="regular"
                :class="{
                  'text-brand-primary':
                    symbol.referencePriceVariationPercentage > 0,
                  'text-critical-primary':
                    symbol.referencePriceVariationPercentage < 0,
                  'text-neutral-secondary':
                    symbol.referencePriceVariationPercentage === 0,
                }"
              >
                ({{ number }})
              </NText>
            </NumberFormatter>
            <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
              <ChangePriceEffect :price="symbol.lastTradePrice">
                <NText
                  :key="symbol.lastTradePrice"
                  class="text-neutral-primary ps-0.5"
                  variant="body"
                  weight="bold"
                >
                  {{ number }}
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
            <NText
              variant="caption1"
              weight="regular"
              class="text-neutral-tertiary align-middle ps-0.5"
            >
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div class="flex flex-col justify-end items-end">
          <NText
            variant="overline"
            weight="medium"
            class="block text-neutral-secondary"
          >
            {{ t('modules.portfolio.net_average') }}
          </NText>

          <div class="flex">
            <NumberFormatter #="{ number }" :value="symbol.netAveragePrice">
              <ChangePriceEffect :price="symbol.netAveragePrice">
                <NText
                  :key="symbol.netAveragePrice"
                  variant="body"
                  weight="bold"
                >
                  {{ number }}
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
            <NText
              variant="caption1"
              class="text-neutral-tertiary align-middle ps-0.5"
            >
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>
      </div>

      <VeeForm v-slot="{ errors, handleSubmit }" as="">
        <div
          class="h-[491.5px] bg-critical-secondary bg-opacity-90 border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10 darkTheme:bg-opacity-50 p-3 rounded-md w-full flex-2 overflow-auto overflow-x-hidden"
        >
          <form
            id="createSltpForm"
            autocomplete="off"
            @submit="handleSubmit($event, onSubmit)"
          >
            <VeeField
              v-slot="{ field }"
              v-model="formModel.percent"
              name="percent"
              :rules="{
                required: true,
                between: [1, MAXIMUM_SLTP_SELL_QUANTITY_PERCENT],
              }"
            >
              <NInput
                v-mask="{
                  alias: 'positiveNumber',
                }"
                :model-value="formModel.percent"
                v-bind="field"
                input-class="text-center font-bold"
                :error="errors.percent"
                :required-sign="false"
                :show-error="true"
                type="tel"
                dir="ltr"
                size="small"
                :min="1"
                :max="MAXIMUM_SLTP_SELL_QUANTITY_PERCENT"
                required
                @focusin="$event.target.select()"
                @update:model-value="changePercent($event)"
              >
                <template #prefix>
                  <NText
                    variant="caption1"
                    weight="medium"
                    class="text-neutral-primary whitespace-nowrap"
                  >
                    {{ t('modules.order.percentageOfAssets') }}
                  </NText>
                </template>

                <template #postfix>
                  <NText variant="caption1" class="text-neutral-secondary">
                    %
                  </NText>
                </template>
              </NInput>
            </VeeField>

            <div class="flex justify-between items-center">
              <NText variant="overline">
                {{ `${t('modules.order.salableQuantity')}:` }}
              </NText>

              <div>
                <NumberFormatter #="{ number }" :value="salableQuantity">
                  <NText variant="overline">
                    {{ number }}
                  </NText>
                </NumberFormatter>
              </div>
            </div>

            <Slider
              :model-value="formModel.percent ?? 0"
              :max-value="MAXIMUM_SLTP_SELL_QUANTITY_PERCENT"
              color="red"
              @change="changePercent"
            />

            <div
              v-if="formModel.percent || formModel.percent === 0"
              class="rounded-md border border-critical-secondary py-1 px-2 whitespace-nowrap mb-4"
            >
              <NText variant="overline" class="text-neutral-primary">
                <i18n-t
                  scope="global"
                  keypath="modules.order.sltpPercentWarning"
                  tag="span"
                >
                  <template #percent>
                    <NText variant="overline" weight="bold">
                      {{
                        t('modules.order.percentValue', {
                          percent: formModel.percent,
                        })
                      }}
                    </NText>
                  </template>
                </i18n-t>
              </NText>
            </div>

            <NCheckbox
              v-model="formModel.upperTriggerChecked"
              :disabled="
                formModel.upperTriggerChecked && !formModel.lowerTriggerChecked
              "
              class="w-full"
              @change="changeCheckbox($event.target.value, 'upperTrigger')"
            >
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary whitespace-nowrap"
              >
                {{ t('modules.order.profitLimit') }}
              </NText>
            </NCheckbox>

            <VeeField
              v-if="formModel.upperTriggerChecked"
              v-slot="{ field }"
              v-model="formModel.upperTrigger"
              name="upperTrigger"
              :rules="{
                required: true,
                min: [0, t('modules.order.profitLimit')],
                equalMin: upperTriggerMinValidation,
              }"
            >
              <NInput
                v-mask="{
                  alias: 'positiveNumber',
                  showMaskOnHover: false,
                }"
                :model-value="formModel.upperTrigger"
                input-class="text-right text-neutral-primary"
                v-bind="field"
                :required-sign="false"
                :show-error="true"
                :error="errors.upperTrigger"
                type="tel"
                dir="ltr"
                class="!mb-0"
                required
                :min="1"
                size="small"
                :placeholder="t('modules.order.profitLimitPrice')"
                @focusin="$event.target.select()"
              >
                <template #postfix>
                  <NText variant="caption1" class="text-neutral-secondary">
                    {{ t('misc.rial') }}
                  </NText>
                </template>
              </NInput>
            </VeeField>

            <NCheckbox
              v-model="formModel.lowerTriggerChecked"
              :disabled="
                formModel.lowerTriggerChecked && !formModel.upperTriggerChecked
              "
              class="my-2"
              @change="changeCheckbox($event.target.value, 'lowerTrigger')"
            >
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary whitespace-nowrap"
              >
                {{ t('modules.order.limitOfLoss') }}
              </NText>
            </NCheckbox>

            <VeeField
              v-if="formModel.lowerTriggerChecked"
              v-slot="{ field }"
              v-model="formModel.lowerTrigger"
              name="lowerTrigger"
              :rules="{
                required: formModel.lowerTriggerChecked,
                min: [0, t('modules.order.limitOfLoss')],
                equalMax: lowerTriggerMaxValidation,
              }"
            >
              <NInput
                v-mask="{
                  alias: 'positiveNumber',
                  showMaskOnHover: false,
                }"
                :model-value="formModel.lowerTrigger"
                v-bind="field"
                input-class="text-right text-neutral-primary"
                :error="errors.lowerTrigger"
                :required-sign="false"
                :show-error="true"
                type="tel"
                dir="ltr"
                :placeholder="t('modules.order.limitOfLossPrice')"
                class="!mb-0"
                size="small"
                :min="1"
                required
                @focusin="$event.target.select()"
              >
                <template #postfix>
                  <NText variant="caption1" class="text-neutral-secondary">
                    {{ t('misc.rial') }}
                  </NText>
                </template>
              </NInput>
            </VeeField>

            <div class="flex mt-3">
              <NText
                weight="medium"
                variant="overline"
                class="text-neutral-secondary"
              >
                {{ t('modules.order.SLTPMessage') }}
              </NText>

              <NegarInfoWithBorderIcon
                class="ps-0.5 cursor-pointer items-center"
                size="16"
                @click="openMarketPriceDialog"
              />
            </div>

            <ConditionalValidity
              v-model="formModel.validityDate"
              class="mt-5 !mb-0"
            />
          </form>
        </div>
      </VeeForm>

      <div cl>
        <NButton
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'SLTPForm',
            component: 'SendSLTPButton',
          }"
          variant="fill"
          class="font-bold text-c1 w-full"
          color="red"
          :loading="isLoadingCreateSLTPOrder"
          size="small"
          form="createSltpForm"
        >
          {{ t('modules.order.sendSLTP') }}
        </NButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NegarErrorBoldIcon, NegarInfoWithBorderIcon } from '@negar/icons';
import { NBox, NText, useTutorialDialog } from '@negar/core';
import { useI18n } from 'vue-i18n';
import { PortfolioItem } from '@libs/api-reference';
import {
  calcMaximumQuantityInPortfolio,
  useLiveOrdersQuery,
  MAXIMUM_SLTP_SELL_QUANTITY_PERCENT,
} from '@core/order';
import { computed, reactive } from 'vue';

import vMask from '@/directives/mask';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

import { useSltpTutorialSteps } from '../../composables/useSltpTutorialSteps';

const { steps: tutorialSteps } = useSltpTutorialSteps();

import {
  useCreateOrder,
  useOrderDialogs,
  SLTP_ORDER_EXECUTION_TYPE,
  ConditionalValidity,
} from '@/modules/order';
import Slider from '@/components/Slider.vue';

const { t } = useI18n();

type SLTPFormDialogPropsType = {
  instrumentName: string;
  symbol: PortfolioItem;
  portfolioQuantity: number;
};

const props = defineProps<SLTPFormDialogPropsType>();

type FormModel = {
  validityDate: number;
  percent: number;
  upperTrigger: number | null;
  upperTriggerChecked: boolean;
  lowerTrigger: number | null;
  lowerTriggerChecked: boolean;
};

const BASE_ORDER_FORM: FormModel = {
  validityDate: 90,
  percent: MAXIMUM_SLTP_SELL_QUANTITY_PERCENT,
  upperTrigger: null,
  upperTriggerChecked: true,
  lowerTrigger: null,
  lowerTriggerChecked: true,
};

const formModel = reactive<FormModel>({ ...BASE_ORDER_FORM });

const { orders } = useLiveOrdersQuery();

const {
  openConfirmSLTPOrderDialog,
  openAlgorithmOrderRiskDescriptionDialog,
  openMarketPriceDialog,
} = useOrderDialogs();

const { createSLTPOrder, isLoadingCreateSLTPOrder } = useCreateOrder({
  isin: props.symbol.instrumentIsin ?? '',
  orderType: 'sell',
});

const salableQuantity = computed(() => {
  const quantityOfPortfolio = props.symbol.quantity;

  return calcMaximumQuantityInPortfolio({
    orders: orders.value,
    quantityOfPortfolio,
  });
});

const changePercent = (val: number) => {
  formModel.percent = val;
};

const changeCheckbox = (val: string, key: string) => {
  const parsed = val === 'true';

  if (!parsed) {
    formModel[key] = null;
  }
};

const upperTriggerMinValidation = computed(() => {
  return {
    min: props.symbol.lastTradePrice,
    type: t('modules.order.profitLimit'),
    minMessage: t('modules.portfolio.current_price'),
  };
});

const lowerTriggerMaxValidation = computed(() => {
  return {
    max: props.symbol.lastTradePrice,
    type: t('modules.order.limitOfLoss'),
    maxMessage: t('modules.portfolio.current_price'),
  };
});

const onSubmit = (model, { resetForm }) => {
  const baseFormModel = {
    percent: formModel.percent,
    upperTrigger: Number(formModel.upperTrigger),
    lowerTrigger: Number(formModel.lowerTrigger),
    validityDate: formModel.validityDate,
  };

  openConfirmSLTPOrderDialog({
    formModel: {
      ...baseFormModel,
    },
    isLoading: isLoadingCreateSLTPOrder,
    confirmCreateSLTPOrder: () =>
      createSLTPOrder({
        formModel: {
          orderExecutionType: SLTP_ORDER_EXECUTION_TYPE,
          ...baseFormModel,
        },
        resetFormModel: resetForm,
        shouldCloseDialog: true,
      }),
  });
};

const { openTutorialDialog: openTutorialDialogFn } = useTutorialDialog();

const openTutorialDialog = () => {
  openTutorialDialogFn({
    steps: tutorialSteps.value,
  });
};
</script>
